import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import { getActiveIndex, getChapters } from "../utils/chapters";
import "./playerControls.cmp.scss";

const RATES = [0.75, 1, 1.25, 1.5, 2];
const HIDE_DELAY = 2500; // ms senza movimento prima di nascondere i controlli
const SNAP_PX = 8; // distanza in px entro cui il cursore si aggancia all'inizio di una sezione

// 65 => "1:05", 3725 => "1:02:05"
const fmt = secs => {
	const s = Math.max(0, Math.floor(secs || 0));
	const h = Math.floor(s / 3600);
	const m = Math.floor((s % 3600) / 60);
	const ss = String(s % 60).padStart(2, "0");
	return h ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
};

const clamp01 = n => Math.min(1, Math.max(0, n));

const CmpPlayerControls = ({ videoRef, containerRef, video }) => {
	const [media, setMedia] = useState({ current: 0, duration: 0, buffered: 0, paused: true, volume: 1, muted: false, rate: 1 });
	const [fullscreen, setFullscreen] = useState(false);
	const [active, setActive] = useState(true);
	const [hover, setHover] = useState(null); // { x, time }
	const [scrub, setScrub] = useState(null); // tempo durante il trascinamento
	const segRefs = useRef([]);
	const seekRef = useRef();
	const hideTimer = useRef();

	const chapters = getChapters(video);
	const duration = media.duration || (chapters.length ? chapters[chapters.length - 1].end : 0);

	// Segmenti contigui della barra: uno per capitolo, oppure uno unico
	const segments = chapters.length
		? chapters.map((c, i) => ({
			start: i === 0 ? 0 : c.start,
			end: i === chapters.length - 1 ? Math.max(duration, c.end) : chapters[i + 1].start,
			chapter: c
		}))
		: [{ start: 0, end: duration, chapter: null }];

	const shownTime = scrub ?? media.current;

	// Sincronizza lo stato con l'elemento <video>
	useEffect(() => {
		const v = videoRef.current;
		if (!v) return;

		const sync = () => {
			let buffered = 0;
			for (let i = 0; i < v.buffered.length; i++) {
				if (v.buffered.start(i) <= v.currentTime) buffered = v.buffered.end(i);
			}
			setMedia({
				current: v.currentTime,
				duration: isFinite(v.duration) ? v.duration : 0,
				buffered,
				paused: v.paused,
				volume: v.volume,
				muted: v.muted,
				rate: v.playbackRate
			});
		};

		const events = ["timeupdate", "progress", "play", "pause", "volumechange", "ratechange", "loadedmetadata", "durationchange", "seeked", "ended"];
		events.forEach(e => v.addEventListener(e, sync));
		v.addEventListener("click", togglePlay);
		v.addEventListener("dblclick", toggleFullscreen);
		sync();

		return () => {
			events.forEach(e => v.removeEventListener(e, sync));
			v.removeEventListener("click", togglePlay);
			v.removeEventListener("dblclick", toggleFullscreen);
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [videoRef, video?.videoId]);

	// Auto-hide dei controlli + scorciatoie da tastiera sul contenitore
	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;

		const poke = () => {
			setActive(true);
			clearTimeout(hideTimer.current);
			hideTimer.current = setTimeout(() => setActive(false), HIDE_DELAY);
		};
		const leave = () => { clearTimeout(hideTimer.current); setActive(false); };

		const onKey = e => {
			const v = videoRef.current;
			if (!v || e.target.tagName === "INPUT") return;
			const actions = {
				" ": togglePlay,
				k: togglePlay,
				ArrowLeft: () => seekTo(v.currentTime - 5),
				ArrowRight: () => seekTo(v.currentTime + 5),
				ArrowUp: () => setVolume(v.volume + 0.1),
				ArrowDown: () => setVolume(v.volume - 0.1),
				f: toggleFullscreen,
				m: toggleMute
			};
			const action = actions[e.key];
			if (!action) return;
			e.preventDefault();
			action();
			poke();
		};

		const onFsChange = () => setFullscreen(document.fullscreenElement === el);

		poke();
		el.addEventListener("mousemove", poke);
		el.addEventListener("touchstart", poke, { passive: true });
		el.addEventListener("mouseleave", leave);
		el.addEventListener("keydown", onKey);
		document.addEventListener("fullscreenchange", onFsChange);

		return () => {
			clearTimeout(hideTimer.current);
			el.removeEventListener("mousemove", poke);
			el.removeEventListener("touchstart", poke);
			el.removeEventListener("mouseleave", leave);
			el.removeEventListener("keydown", onKey);
			document.removeEventListener("fullscreenchange", onFsChange);
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [containerRef]);

	const togglePlay = () => {
		const v = videoRef.current;
		if (!v) return;
		v.paused || v.ended ? v.play().catch(() => {}) : v.pause();
	};

	const seekTo = time => {
		const v = videoRef.current;
		if (v) v.currentTime = Math.min(Math.max(0, time), v.duration || time);
	};

	const setVolume = vol => {
		const v = videoRef.current;
		if (!v) return;
		v.volume = clamp01(vol);
		v.muted = v.volume === 0;
	};

	const toggleMute = () => {
		const v = videoRef.current;
		if (!v) return;
		v.muted = !v.muted;
		if (!v.muted && v.volume === 0) v.volume = 0.5;
	};

	const cycleRate = () => {
		const v = videoRef.current;
		if (!v) return;
		v.playbackRate = RATES[(RATES.indexOf(v.playbackRate) + 1) % RATES.length] || 1;
	};

	const toggleFullscreen = () => {
		const el = containerRef.current;
		const v = videoRef.current;
		if (document.fullscreenElement) return document.exitFullscreen();
		if (el?.requestFullscreen) return el.requestFullscreen().catch(() => {});
		if (v?.webkitEnterFullscreen) v.webkitEnterFullscreen(); // iOS Safari
	};

	// Tempo corrispondente a una coordinata X della barra, con aggancio all'inizio delle sezioni
	const timeAt = clientX => {
		const rects = segRefs.current.map(el => el?.getBoundingClientRect());
		let i = rects.findIndex(r => r && clientX <= r.right);
		if (i === -1) i = rects.length - 1;
		const r = rects[i];
		const seg = segments[i];
		if (!r || !seg) return 0;

		const len = seg.end - seg.start;
		let time = seg.start + clamp01((clientX - r.left) / r.width) * len;

		const starts = seg.chapter ? seg.chapter.sections.map(s => s.start) : [];
		for (const start of starts) {
			const px = r.left + ((start - seg.start) / (len || 1)) * r.width;
			if (Math.abs(px - clientX) <= SNAP_PX) { time = start; break; }
		}
		return time;
	};

	const hoverAt = e => {
		const rect = seekRef.current.getBoundingClientRect();
		return { x: e.clientX - rect.left, width: rect.width, time: timeAt(e.clientX) };
	};

	const onPointerDown = e => {
		if (e.button !== 0) return;
		e.currentTarget.setPointerCapture(e.pointerId);
		const h = hoverAt(e);
		setHover(h);
		setScrub(h.time);
	};

	const onPointerMove = e => {
		const h = hoverAt(e);
		setHover(h);
		if (scrub !== null) setScrub(h.time);
	};

	const onPointerUp = e => {
		if (scrub === null) return;
		seekTo(timeAt(e.clientX));
		setScrub(null);
	};

	const describe = time => {
		const cIdx = getActiveIndex(chapters, time);
		const c = chapters[cIdx];
		if (!c) return null;
		const sIdx = getActiveIndex(c.sections, time);
		const s = c.sections[sIdx];
		return {
			chapter: `Capitolo ${cIdx + 1}: ${c.title}`,
			section: s ? `${cIdx + 1}.${sIdx + 1} ${s.title}` : null
		};
	};

	const current = describe(shownTime);
	const hovered = hover ? describe(hover.time) : null;
	const hoverSegIdx = hover ? segments.findIndex(s => hover.time >= s.start && hover.time < s.end) : -1;

	const visible = media.paused || active || scrub !== null;
	const volumeIcon = media.muted || media.volume === 0 ? "volume-mute" : media.volume < 0.5 ? "volume-down" : "volume-up";

	const scrollToToc = () =>
		document.querySelector(".toc article.active")?.scrollIntoView({ behavior: "smooth", block: "center" });

	return <>
		{
			media.paused && scrub === null ?
			<button className = "player-center-play" onClick = {togglePlay} title = "Riproduci (k)" aria-label = "Riproduci">
				<FontAwesomeIcon icon = "play"/>
			</button> : null
		}

		<div className = {`cmp-player-controls ${visible ? "" : "hidden"}`}>
			<div
				className = {`seek ${scrub !== null ? "scrubbing" : ""}`}
				ref = {seekRef}
				onPointerDown = {onPointerDown}
				onPointerMove = {onPointerMove}
				onPointerUp = {onPointerUp}
				onPointerLeave = {() => scrub === null && setHover(null)}
				role = "slider"
				aria-label = "Posizione nel video"
				aria-valuemin = {0}
				aria-valuemax = {Math.floor(duration)}
				aria-valuenow = {Math.floor(shownTime)}
				aria-valuetext = {fmt(shownTime)}
			>
				{
					hover ?
					<div className = "seek-tooltip" style = {{ left: Math.min(Math.max(hover.x, 80), hover.width - 80) }}>
						{hovered ? <>
							<div className = "tt-chapter">{hovered.chapter}</div>
							{hovered.section ? <div className = "tt-section">{hovered.section}</div> : null}
						</> : null}
						<div className = "tt-time">{fmt(hover.time)}</div>
					</div> : null
				}

				<div className = "segments">
					{segments.map((s, i) => {
						const len = Math.max(0.001, s.end - s.start);
						const played = clamp01((shownTime - s.start) / len) * 100;
						const buffered = clamp01((media.buffered - s.start) / len) * 100;
						const isCurrent = shownTime >= s.start && (shownTime < s.end || i === segments.length - 1);

						return <div
							key = {i}
							ref = {el => segRefs.current[i] = el}
							className = {`segment ${i === hoverSegIdx ? "hovered" : ""}`}
							style = {{ flexGrow: len }}
						>
							<span className = "buffered" style = {{ width: `${buffered}%` }}/>
							<span className = "played" style = {{ width: `${played}%` }}/>
							{(s.chapter?.sections || []).slice(1).map((sec, j) =>
								<span key = {j} className = "tick" style = {{ left: `${((sec.start - s.start) / len) * 100}%` }}/>
							)}
							{isCurrent ? <span className = "handle" style = {{ left: `${played}%` }}/> : null}
						</div>;
					})}
				</div>
			</div>

			<div className = "bar">
				<button onClick = {togglePlay} title = {media.paused ? "Riproduci (k)" : "Pausa (k)"}>
					<FontAwesomeIcon icon = {media.paused ? "play" : "pause"} fixedWidth/>
				</button>

				<div className = "volume hide-mini">
					<button onClick = {toggleMute} title = {media.muted ? "Riattiva audio (m)" : "Disattiva audio (m)"}>
						<FontAwesomeIcon icon = {volumeIcon} fixedWidth/>
					</button>
					<input
						type = "range" min = "0" max = "1" step = "0.05"
						value = {media.muted ? 0 : media.volume}
						onChange = {e => setVolume(Number(e.target.value))}
						aria-label = "Volume"
						style = {{ "--vol": `${(media.muted ? 0 : media.volume) * 100}%` }}
					/>
				</div>

				<span className = "time">{fmt(shownTime)} / {fmt(duration)}</span>

				{
					current ?
					<button className = "chapter-label hide-mini" onClick = {scrollToToc} title = "Vai all'indice">
						<span className = "sep">•</span>
						<span className = "text">{current.chapter}{current.section ? ` › ${current.section}` : ""}</span>
						<FontAwesomeIcon icon = "chevron-right" className = "chev"/>
					</button> : <span className = "spacer-flex"/>
				}

				<button className = "rate hide-mini" onClick = {cycleRate} title = "Velocità di riproduzione">
					{media.rate}x
				</button>

				<button onClick = {toggleFullscreen} title = {fullscreen ? "Esci da schermo intero (f)" : "Schermo intero (f)"}>
					<FontAwesomeIcon icon = {fullscreen ? "compress" : "expand"} fixedWidth/>
				</button>
			</div>
		</div>
	</>;
};

export default CmpPlayerControls;
